import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const COLLAPSED_STORAGE_KEY = "nymbl:sidebar-collapsed";

interface SidebarContextValue {
  mobileOpen: boolean;
  setMobileOpen: (open: boolean) => void;
  isMobile: boolean;
  collapsed: boolean;
  toggleCollapsed: () => void;
}

const SidebarContext = createContext<SidebarContextValue>({
  mobileOpen: false,
  setMobileOpen: () => {},
  isMobile: false,
  collapsed: false,
  toggleCollapsed: () => {},
});

export function SidebarProvider({ children }: { children: React.ReactNode }) {
  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSED_STORAGE_KEY) === "true",
  );

  useEffect(() => {
    const mql = window.matchMedia("(max-width: 767px)");

    function handleChange(e: MediaQueryListEvent | MediaQueryList) {
      const mobile = e.matches;
      setIsMobile(mobile);
      if (!mobile) {
        setMobileOpen(false);
      }
    }

    // Set initial value
    handleChange(mql);

    mql.addEventListener("change", handleChange);
    return () => mql.removeEventListener("change", handleChange);
  }, []);

  const handleSetMobileOpen = useCallback((open: boolean) => {
    setMobileOpen(open);
  }, []);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(COLLAPSED_STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      mobileOpen,
      setMobileOpen: handleSetMobileOpen,
      isMobile,
      collapsed,
      toggleCollapsed,
    }),
    [mobileOpen, handleSetMobileOpen, isMobile, collapsed, toggleCollapsed],
  );

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}

export function useSidebar() {
  return useContext(SidebarContext);
}
